/**
 * @deprecated Standalone email store — replaced by unified Conversations UI.
 * Kept for reference; pending removal in a future release.
 */
import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "../components/ui/toast";
import emailService, {
  type EmailDomain,
  type EmailSender,
  type EmailMessage,
  type DeliveryEvent,
  type SendEmailPayload,
  type SendEmailResponse,
  type SenderQuotaUsage,
  type MessageFilters,
  type DnsStatusResponse,
  type DomainHealthResponse,
  type RegisterDomainDto,
  type RegisterSenderDto,
  type UpdateSenderDto,
  type EmailSenderStatus,
  type EmailConversation,
  type ConversationMessage,
  type ConversationFilters,
  type ConversationStatus,
  type UnreadSummary,
} from "../services/email.service";

export default class EmailStore {
  // ── Domain State ──────────────────────────────────────────────────────
  domains: EmailDomain[] = [];
  domainsTotal = 0;
  domainsPage = 1;
  domainsLimit = 10;
  currentDomain: EmailDomain | null = null;
  dnsStatus: DnsStatusResponse | null = null;
  domainHealth: DomainHealthResponse | null = null;

  // ── Sender State ──────────────────────────────────────────────────────
  senders: EmailSender[] = [];
  sendersTotal = 0;
  sendersPage = 1;
  sendersLimit = 10;

  // ── Message State ─────────────────────────────────────────────────────
  messages: EmailMessage[] = [];
  messagesTotal = 0;
  nextCursor: string | null = null;
  prevCursor: string | null = null;
  selectedMessage: EmailMessage | null = null;
  deliveryEvents: Record<string, DeliveryEvent[]> = {};

  // ── Loading State ─────────────────────────────────────────────────────
  isLoading = false;
  isDnsLoading = false;
  isSendersLoading = false;
  isMessagesLoading = false;
  isSendingMessage = false;

  // ── Conversation / Inbox State ────────────────────────────────────────
  conversations: EmailConversation[] = [];
  conversationsTotal = 0;
  conversationsPage = 1;
  conversationsLimit = 20;
  selectedConversation: EmailConversation | null = null;
  conversationMessages: ConversationMessage[] = [];
  conversationMessagesTotal = 0;
  conversationMessagesPage = 1;
  unreadSummary: UnreadSummary = { totalUnread: 0, conversationsWithUnread: 0 };
  isConversationsLoading = false;
  isConversationMessagesLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Domain Actions
  // ═══════════════════════════════════════════════════════════════════════

  fetchDomains = async (page?: number, limit?: number) => {
    this.isLoading = true;
    try {
      const response = await emailService.getDomains({
        page: page ?? this.domainsPage,
        limit: limit ?? this.domainsLimit,
      });
      runInAction(() => {
        this.domains = response.data.data;
        this.domainsTotal = response.data.total;
        this.domainsPage = response.data.page;
        this.domainsLimit = response.data.limit;
      });
    } catch (error) {
      toast.error("Failed to fetch domains");
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  registerDomain = async (dto: RegisterDomainDto): Promise<EmailDomain | null> => {
    this.isLoading = true;
    try {
      const response = await emailService.registerDomain(dto);
      runInAction(() => {
        this.domains = [response.data, ...this.domains];
        this.domainsTotal += 1;
      });
      toast.success("Domain registered successfully");
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error("This domain is already registered for your organization");
      } else {
        toast.error("Failed to register domain");
      }
      return null;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  fetchDomain = async (id: string) => {
    this.isLoading = true;
    try {
      const response = await emailService.getDomain(id);
      runInAction(() => {
        this.currentDomain = response.data;
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error("Domain not found");
      } else {
        toast.error("Failed to fetch domain");
      }
      runInAction(() => {
        this.currentDomain = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  fetchDnsStatus = async (id: string) => {
    this.isDnsLoading = true;
    try {
      const response = await emailService.getDnsStatus(id);
      runInAction(() => {
        this.dnsStatus = response.data;
      });
    } catch (error) {
      // Silently fail on polling — don't spam toasts
    } finally {
      runInAction(() => {
        this.isDnsLoading = false;
      });
    }
  };

  verifyDomain = async (id: string) => {
    try {
      const response = await emailService.verifyDomain(id);
      runInAction(() => {
        this.currentDomain = response.data;
        // Update the domain in the list as well
        this.domains = this.domains.map((d) =>
          d.id === id ? response.data : d,
        );
      });
      toast.success("Verification initiated");
    } catch (error: any) {
      if (error?.response?.status === 422) {
        toast.error("Domain is not in a valid state for verification");
      } else {
        toast.error("Failed to initiate verification");
      }
    }
  };

  deleteDomain = async (id: string): Promise<boolean> => {
    try {
      await emailService.deleteDomain(id);
      runInAction(() => {
        this.domains = this.domains.filter((d) => d.id !== id);
        this.domainsTotal -= 1;
        if (this.currentDomain?.id === id) {
          this.currentDomain = null;
        }
      });
      toast.success("Domain removed successfully");
      return true;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error("Domain not found");
      } else {
        toast.error("Failed to remove domain");
      }
      return false;
    }
  };

  fetchDomainHealth = async (id: string) => {
    try {
      const response = await emailService.getDomainHealth(id);
      runInAction(() => {
        this.domainHealth = response.data;
      });
    } catch (error) {
      toast.error("Failed to fetch domain health");
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Sender Actions
  // ═══════════════════════════════════════════════════════════════════════

  fetchSenders = async (params?: {
    page?: number;
    limit?: number;
    domainId?: string;
    status?: EmailSenderStatus;
  }) => {
    this.isSendersLoading = true;
    try {
      const response = await emailService.getSenders({
        page: params?.page ?? this.sendersPage,
        limit: params?.limit ?? this.sendersLimit,
        domainId: params?.domainId,
        status: params?.status,
      });
      runInAction(() => {
        this.senders = response.data.data;
        this.sendersTotal = response.data.total;
        this.sendersPage = response.data.page;
        this.sendersLimit = response.data.limit;
      });
    } catch (error) {
      toast.error("Failed to fetch senders");
    } finally {
      runInAction(() => {
        this.isSendersLoading = false;
      });
    }
  };

  registerSender = async (dto: RegisterSenderDto): Promise<EmailSender | null> => {
    this.isSendersLoading = true;
    try {
      const response = await emailService.registerSender(dto);
      runInAction(() => {
        this.senders = [response.data, ...this.senders];
        this.sendersTotal += 1;
      });
      toast.success(`Sender ${dto.emailAddress} added successfully.`);
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.message;
      if (status === 409) {
        toast.error("This sender address is already registered");
      } else if (status === 422 && errorCode === "DOMAIN_NOT_ACTIVE") {
        toast.error("Domain must be fully verified before adding senders");
      } else if (status === 422 && errorCode === "EMAIL_DOMAIN_MISMATCH") {
        toast.error("Email address must use the registered domain");
      } else {
        toast.error("Failed to register sender");
      }
      return null;
    } finally {
      runInAction(() => {
        this.isSendersLoading = false;
      });
    }
  };

  resendSenderVerification = async (senderId: string, emailAddress: string) => {
    try {
      await emailService.resendSenderVerification(senderId);
      toast.success(`Verification email resent to ${emailAddress}`);
    } catch (error) {
      toast.error("Failed to resend verification email");
    }
  };

  deleteSender = async (senderId: string): Promise<boolean> => {
    try {
      await emailService.deleteSender(senderId);
      runInAction(() => {
        this.senders = this.senders.filter((s) => s.id !== senderId);
        this.sendersTotal -= 1;
      });
      toast.success("Sender removed successfully");
      return true;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error("Sender not found");
      } else {
        toast.error("Failed to remove sender");
      }
      return false;
    }
  };

  updateSender = async (senderId: string, dto: UpdateSenderDto): Promise<EmailSender | null> => {
    try {
      const response = await emailService.updateSender(senderId, dto);
      runInAction(() => {
        this.senders = this.senders.map((s) =>
          s.id === senderId ? response.data : s,
        );
      });
      toast.success("Sender updated successfully");
      return response.data;
    } catch (error) {
      toast.error("Failed to update sender");
      return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Message Actions
  // ═══════════════════════════════════════════════════════════════════════

  fetchMessages = async (filters?: MessageFilters) => {
    this.isMessagesLoading = true;
    try {
      const response = await emailService.getMessages(filters);
      runInAction(() => {
        this.messages = response.data.data;
        this.messagesTotal = response.data.total;
        this.nextCursor = response.data.nextCursor;
        this.prevCursor = response.data.prevCursor;
      });
    } catch (error) {
      toast.error("Failed to fetch messages");
    } finally {
      runInAction(() => {
        this.isMessagesLoading = false;
      });
    }
  };

  getMessage = async (id: string) => {
    this.isMessagesLoading = true;
    try {
      const response = await emailService.getMessage(id);
      runInAction(() => {
        this.selectedMessage = response.data;
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error("Message not found");
      } else {
        toast.error("Failed to fetch message");
      }
      runInAction(() => {
        this.selectedMessage = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isMessagesLoading = false;
      });
    }
  };

  sendMessage = async (payload: SendEmailPayload): Promise<SendEmailResponse | null> => {
    this.isSendingMessage = true;
    try {
      const response = await emailService.sendMessage(payload);
      toast.success("Email sent successfully");
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 429) {
        const resetAt = error?.response?.headers?.["x-ratelimit-reset"];
        const resetTime = resetAt
          ? new Date(Number(resetAt) * 1000).toLocaleTimeString()
          : "midnight";
        toast.error(`Daily quota reached. Resets at ${resetTime}.`);
      } else if (error?.response?.status === 422) {
        const msg = error?.response?.data?.message;
        toast.error(msg || "Validation error");
      } else {
        toast.error("Failed to send email. Please try again.");
      }
      return null;
    } finally {
      runInAction(() => {
        this.isSendingMessage = false;
      });
    }
  };

  fetchDeliveryEvents = async (messageId: string) => {
    try {
      const response = await emailService.getDeliveryEvents(messageId);
      runInAction(() => {
        this.deliveryEvents[messageId] = response.data;
      });
    } catch (error) {
      // Silently fail — delivery events are supplementary
    }
  };

  applyDeliveryEvent = (event: DeliveryEvent) => {
    // Update message status in the messages list
    this.messages = this.messages.map((m) =>
      m.id === event.messageId
        ? { ...m, deliveryStatus: event.type.toUpperCase() as EmailMessage["deliveryStatus"] }
        : m,
    );

    // Update selected message if viewing the same message
    if (this.selectedMessage?.id === event.messageId) {
      this.selectedMessage = {
        ...this.selectedMessage,
        deliveryStatus: event.type.toUpperCase() as EmailMessage["deliveryStatus"],
      };
    }

    // Append event to delivery events for that message
    const existing = this.deliveryEvents[event.messageId] || [];
    this.deliveryEvents[event.messageId] = [...existing, event];
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Conversation / Inbox Actions
  // ═══════════════════════════════════════════════════════════════════════

  fetchConversations = async (filters?: ConversationFilters) => {
    this.isConversationsLoading = true;
    try {
      const response = await emailService.getConversations({
        page: filters?.page ?? this.conversationsPage,
        limit: filters?.limit ?? this.conversationsLimit,
        ...filters,
      });
      runInAction(() => {
        this.conversations = response.data.data;
        this.conversationsTotal = response.data.total;
        this.conversationsPage = response.data.page;
        this.conversationsLimit = response.data.limit;
      });
    } catch (error) {
      toast.error("Failed to fetch conversations");
    } finally {
      runInAction(() => {
        this.isConversationsLoading = false;
      });
    }
  };

  fetchConversation = async (id: string) => {
    this.isConversationsLoading = true;
    try {
      const response = await emailService.getConversation(id);
      runInAction(() => {
        this.selectedConversation = response.data;
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error("Conversation not found");
      } else {
        toast.error("Failed to fetch conversation");
      }
      runInAction(() => {
        this.selectedConversation = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isConversationsLoading = false;
      });
    }
  };

  fetchConversationMessages = async (conversationId: string, page?: number) => {
    this.isConversationMessagesLoading = true;
    try {
      const response = await emailService.getConversationMessages(conversationId, {
        page: page ?? this.conversationMessagesPage,
        limit: 50,
      });
      runInAction(() => {
        this.conversationMessages = response.data.data;
        this.conversationMessagesTotal = response.data.total;
        this.conversationMessagesPage = response.data.page;
      });
    } catch (error) {
      toast.error("Failed to fetch messages");
    } finally {
      runInAction(() => {
        this.isConversationMessagesLoading = false;
      });
    }
  };

  markConversationRead = async (conversationId: string) => {
    try {
      await emailService.markConversationRead(conversationId);
      runInAction(() => {
        // Update in conversation list
        this.conversations = this.conversations.map((c) =>
          c.id === conversationId ? { ...c, unreadCount: 0 } : c,
        );
        // Update selected conversation
        if (this.selectedConversation?.id === conversationId) {
          this.selectedConversation = { ...this.selectedConversation, unreadCount: 0 };
        }
        // Update unread summary
        this.fetchUnreadSummary();
      });
    } catch (error) {
      // Silently fail — mark-read is supplementary
    }
  };

  updateConversationStatus = async (
    conversationId: string,
    status: ConversationStatus,
  ) => {
    try {
      await emailService.updateConversationStatus(conversationId, status);
      runInAction(() => {
        this.conversations = this.conversations.map((c) =>
          c.id === conversationId ? { ...c, status } : c,
        );
        if (this.selectedConversation?.id === conversationId) {
          this.selectedConversation = { ...this.selectedConversation, status };
        }
      });
      toast.success(`Conversation marked as ${status}`);
    } catch (error) {
      toast.error("Failed to update conversation status");
    }
  };

  fetchUnreadSummary = async () => {
    try {
      const response = await emailService.getUnreadSummary();
      runInAction(() => {
        this.unreadSummary = response.data;
      });
    } catch (error) {
      // Silently fail
    }
  };

  /** Called by WebSocket when a new inbound email arrives */
  handleInboundEmail = (payload: {
    conversationId: string;
    isNewConversation: boolean;
    from: string;
    subject: string;
    preview: string;
    timestamp: string;
  }) => {
    // Refresh conversations list and unread summary
    this.fetchConversations();
    this.fetchUnreadSummary();

    // If viewing the same conversation, refresh messages
    if (this.selectedConversation?.id === payload.conversationId) {
      this.fetchConversationMessages(payload.conversationId);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Cleanup
  // ═══════════════════════════════════════════════════════════════════════

  clearCurrentDomain = () => {
    this.currentDomain = null;
    this.dnsStatus = null;
    this.domainHealth = null;
    this.senders = [];
    this.sendersTotal = 0;
  };

  clearSelectedMessage = () => {
    this.selectedMessage = null;
  };

  clearSelectedConversation = () => {
    this.selectedConversation = null;
    this.conversationMessages = [];
    this.conversationMessagesTotal = 0;
    this.conversationMessagesPage = 1;
  };
}
